import React, { useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  type LensSchedule,
  type LensScheduleRun,
  useDeleteLensSchedule,
  useLensSchedules,
  useSaveLensSchedule,
  useSendLensScheduleTest,
} from '~/hooks/apis/reporting/useLensSchedules';

import type { Lens, LensFilterValues, LensPeriod } from '~/types/leLens';

import LensRecipientInput from '../LensRecipientInput';
import {
  COMPARE_TO_PRESETS,
  DATE_RANGE_PRESETS,
  matchingCompareTo,
  matchingDateRangePreset,
} from '../lensPeriodPresets';

import {
  ALLOWED_RECIPIENT,
  SCHEDULE_LIMITS,
  type ScheduleDraft,
  describeFilters,
  draftProblem,
  emptyDraft,
  isFixedPeriod,
  labelFor,
  lensHasCards,
  lensHasFilters,
  mergeSets,
  ordinal,
  screenFilters,
  setsForEachValue,
} from './scheduleForm';

type Props = {
  open: boolean;
  onClose: () => void;
  lens: Lens;
  /** The reader's filter values on screen (saved defaults are added here). */
  screenValues: LensFilterValues;
  /** The values each select filter can take, as the board's filter controls list them. */
  filterOptions: Record<string, Array<string>>;
  userEmail?: string;
};

// "Today so far" means nothing at 8am, so it is not offered.
const RANGE_CHOICES = DATE_RANGE_PRESETS.filter((preset) => preset.id !== 'todaySoFar');
const DEFAULT_ID = 'lensDefault';
const CUSTOM_ID = 'custom';

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong. Try again.');

function runSummary(run: LensScheduleRun | undefined): string {
  if (!run) {
    return 'Not sent yet.';
  }
  const who = run.trigger === 'test' ? 'Test' : 'Sent';
  const files = run.files.filter((file) => !file.error).length;
  switch (run.status) {
    case 'queued':
    case 'running':
      return `${run.trigger === 'test' ? 'Test' : 'Send'} on its way…`;
    case 'sent':
      return `${who} ${run.runDate}: ${files} PDF${files === 1 ? '' : 's'}${run.delivery === 'link' ? ' (as a link, too large to attach)' : ''}.`;
    case 'partial':
      return `${who} ${run.runDate}: ${files} of ${run.files.length} PDFs. Not made: ${run.files
        .filter((file) => file.error)
        .map((file) => file.label)
        .join(', ')}.`;
    default:
      return `Not sent ${run.runDate}: ${run.error ?? 'see the email for why'}.`;
  }
}

function PeriodFields({
  value,
  onChange,
}: {
  value: LensPeriod | null;
  onChange: (period: LensPeriod | null) => void;
}) {
  const rangeId = !value
    ? DEFAULT_ID
    : isFixedPeriod(value)
      ? CUSTOM_ID
      : (matchingDateRangePreset(value)?.id ?? CUSTOM_ID);
  const fixed = value && isFixedPeriod(value) ? (value.window as { from: string; to: string }) : null;
  return (
    <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1.5}>
      <TextField
        select
        label="Date range"
        size="small"
        sx={{ minWidth: 220 }}
        value={rangeId}
        onChange={(event) => {
          const id = event.target.value;
          if (id === DEFAULT_ID) {
            onChange(null);
          } else if (id === CUSTOM_ID) {
            onChange({ window: { from: '', to: '' }, comparison: value?.comparison ?? null });
          } else {
            const preset = RANGE_CHOICES.find((choice) => choice.id === id);
            onChange({
              ...(preset.anchor ? { anchor: preset.anchor } : {}),
              window: preset.window,
              comparison: value?.comparison ?? { back: 1, unit: 'year' },
            });
          }
        }}
        helperText={
          rangeId === DEFAULT_ID
            ? 'Whatever the Lens opens with, worked out on the day it sends.'
            : rangeId === CUSTOM_ID
              ? 'The same dates every time.'
              : 'Worked out on the day it sends.'
        }
      >
        <MenuItem value={DEFAULT_ID}>The Lens’s own default</MenuItem>
        {RANGE_CHOICES.map((preset) => (
          <MenuItem key={preset.id} value={preset.id}>
            {preset.label}
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM_ID}>Fixed dates…</MenuItem>
      </TextField>
      {fixed && (
        <>
          <TextField
            label="From"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fixed.from}
            onChange={(event) => onChange({ ...value, window: { ...fixed, from: event.target.value } })}
          />
          <TextField
            label="To"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fixed.to}
            onChange={(event) => onChange({ ...value, window: { ...fixed, to: event.target.value } })}
          />
        </>
      )}
      {value && (
        <TextField
          select
          label="Compare to"
          size="small"
          sx={{ minWidth: 200 }}
          value={matchingCompareTo(value.comparison)?.id ?? 'none'}
          onChange={(event) =>
            onChange({
              ...value,
              comparison: COMPARE_TO_PRESETS.find((preset) => preset.id === event.target.value)?.comparison ?? null,
            })
          }
        >
          {COMPARE_TO_PRESETS.map((preset) => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

function ScheduleEditor({
  lens,
  initial,
  screenValues,
  filterOptions,
  onCancel,
  onSave,
  saving,
  saveError,
}: {
  lens: Lens;
  initial: ScheduleDraft;
  screenValues: LensFilterValues;
  filterOptions: Record<string, Array<string>>;
  onCancel: () => void;
  onSave: (draft: ScheduleDraft) => void;
  saving: boolean;
  saveError: string | null;
}) {
  const [draft, setDraft] = useState<ScheduleDraft>(initial);
  const [notice, setNotice] = useState<string | null>(null);
  const [eachFilterId, setEachFilterId] = useState('');
  const [eachValues, setEachValues] = useState<Array<string>>([]);
  const [recipient, setRecipient] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const base = useMemo(() => screenFilters(lens, screenValues), [lens, screenValues]);
  const selectFilters = (lens.spec.filters ?? []).filter((filter) => filter.type === 'select');
  const eachFilter = selectFilters.find((filter) => filter.id === eachFilterId);
  const eachOptions = eachFilter
    ? filterOptions[eachFilter.id]?.length
      ? filterOptions[eachFilter.id]
      : (eachFilter.options ?? [])
    : [];
  const problem = draftProblem(draft, lens);
  const hasCards = lensHasCards(lens);
  const hasFilters = lensHasFilters(lens);
  const room = SCHEDULE_LIMITS.filterSets - draft.filterSets.length;
  const update = (patch: Partial<ScheduleDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const addSets = (sets: ScheduleDraft['filterSets']) => {
    const merged = mergeSets(lens, draft.filterSets, sets);
    update({ filterSets: merged.sets });
    setNotice(
      merged.skipped
        ? `${merged.skipped} not added: a PDF with the same name or the same filters is already in the list, or the list is full (${SCHEDULE_LIMITS.filterSets} PDFs).`
        : null,
    );
  };
  const addRecipient = () => {
    const email = recipient.trim().toLowerCase();
    if (!ALLOWED_RECIPIENT.test(email)) {
      setRecipientError('Enter a luxuryescapes.com address.');
      return;
    }
    if (!draft.recipients.includes(email)) {
      update({ recipients: [...draft.recipients, email] });
    }
    setRecipient('');
    setRecipientError(null);
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1.5}>
            <TextField
              fullWidth
              label="Name"
              size="small"
              value={draft.name}
              inputProps={{ maxLength: SCHEDULE_LIMITS.name }}
              onChange={(event) => update({ name: event.target.value })}
              helperText="Starts the email subject and each PDF's file name."
            />
            <TextField
              select
              label="Day of the month"
              size="small"
              sx={{ minWidth: 190 }}
              value={draft.dayOfMonth}
              onChange={(event) => update({ dayOfMonth: Number(event.target.value) })}
              helperText="Sent at 8am Sydney time."
            >
              {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
                <MenuItem key={day} value={day}>
                  {ordinal(day)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box>
            <Typography fontWeight={600} gutterBottom variant="subtitle2">
              Period
            </Typography>
            <PeriodFields value={draft.period} onChange={(period) => update({ period })} />
          </Box>

          <Box>
            <Typography fontWeight={600} variant="subtitle2">
              PDFs ({draft.filterSets.length} of up to {SCHEDULE_LIMITS.filterSets})
            </Typography>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Each PDF is this Lens drawn with different filter values, for example one PDF per partner. All of them
              arrive in one email.
            </Typography>
            {!hasCards && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This Lens has no cards yet, so there is nothing to put in a PDF. Add a card to the Lens first.
              </Alert>
            )}
            {hasCards && !hasFilters && (
              <Alert severity="info" sx={{ mb: 1 }}>
                This Lens has no filters, so every PDF would be the same and you can add only one. To make one PDF per
                partner (or per anything else), add that filter to the Lens first.
              </Alert>
            )}
            <Stack spacing={1}>
              {draft.filterSets.map((set, index) => (
                <Stack alignItems="center" direction="row" key={index} spacing={1}>
                  <TextField
                    label="PDF name"
                    size="small"
                    sx={{ width: 240 }}
                    value={set.label}
                    inputProps={{ maxLength: SCHEDULE_LIMITS.label }}
                    onChange={(event) =>
                      update({
                        filterSets: draft.filterSets.map((item, at) =>
                          at === index ? { ...item, label: event.target.value } : item,
                        ),
                      })
                    }
                  />
                  <Typography color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap variant="body2">
                    {describeFilters(lens, set.filters)}
                  </Typography>
                  <IconButton
                    aria-label={`Remove ${set.label}`}
                    size="small"
                    onClick={() => update({ filterSets: draft.filterSets.filter((_, at) => at !== index) })}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Stack direction={{ md: 'row', xs: 'column' }} spacing={1.5} sx={{ mt: 1.5 }}>
              <Button
                disabled={!hasCards || room <= 0 || (!hasFilters && draft.filterSets.length > 0)}
                variant="outlined"
                size="small"
                onClick={() => addSets([{ label: labelFor(lens, base), filters: base }])}
              >
                {hasFilters ? 'Add the filters on screen' : 'Add the whole Lens'}
              </Button>
              {hasCards && selectFilters.length > 0 && (
                <Stack alignItems="flex-start" direction={{ sm: 'row', xs: 'column' }} spacing={1} sx={{ flex: 1 }}>
                  <TextField
                    select
                    label="One PDF per"
                    size="small"
                    sx={{ minWidth: 160 }}
                    value={eachFilterId}
                    onChange={(event) => {
                      setEachFilterId(event.target.value);
                      setEachValues([]);
                    }}
                  >
                    {selectFilters.map((filter) => (
                      <MenuItem key={filter.id} value={filter.id}>
                        {filter.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  {eachFilter && (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={eachOptions}
                      value={eachValues}
                      onChange={(_, values) => setEachValues(values)}
                      sx={{ flex: 1, minWidth: 220 }}
                      renderInput={(params) => (
                        <TextField {...params} label={`${eachFilter.label} values`} size="small" />
                      )}
                    />
                  )}
                  {eachFilter && (
                    <Button
                      disabled={!eachOptions.length || eachValues.length === eachOptions.length}
                      size="small"
                      onClick={() => setEachValues(eachOptions)}
                    >
                      All {eachOptions.length}
                    </Button>
                  )}
                  {eachFilter && (
                    <Button
                      disabled={!eachValues.length}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        addSets(setsForEachValue(lens, base, eachFilter.id, eachValues));
                        setEachValues([]);
                      }}
                    >
                      Add {eachValues.length || ''} PDF{eachValues.length === 1 ? '' : 's'}
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
            {notice && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {notice}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography fontWeight={600} variant="subtitle2">
              Recipients
            </Typography>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Luxury Escapes addresses only. Check a PDF before passing it on to a partner.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
              {draft.recipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => update({ recipients: draft.recipients.filter((item) => item !== email) })}
                />
              ))}
            </Stack>
            <Stack alignItems="flex-start" direction="row" spacing={1}>
              <LensRecipientInput
                value={recipient}
                error={recipientError}
                excludedEmails={draft.recipients}
                onChange={(value) => {
                  setRecipient(value);
                  setRecipientError(null);
                }}
                onSubmit={addRecipient}
              />
              <Button disabled={!recipient.trim()} onClick={addRecipient} sx={{ mt: 0.5 }} variant="outlined">
                Add
              </Button>
            </Stack>
          </Box>

          {lens.spec.customView && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.includeInternal}
                    onChange={(event) => update({ includeInternal: event.target.checked })}
                  />
                }
                label="Include internal figures (for example vendor revenue)"
              />
              <Typography color={draft.includeInternal ? 'warning.main' : 'text.secondary'} variant="body2">
                {draft.includeInternal
                  ? 'The PDFs will show internal figures and the email will say not to send them to partners.'
                  : 'Off: the view is asked to leave out figures it marks as internal. Check a test send before passing PDFs on.'}
              </Typography>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={draft.enabled} onChange={(event) => update({ enabled: event.target.checked })} />}
            label={draft.enabled ? 'On: sends every month' : 'Paused: will not send'}
          />
          {touched && problem && <Alert severity="warning">{problem}</Alert>}
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Back</Button>
        <Button
          disabled={saving || !hasCards}
          variant="contained"
          onClick={() => {
            setTouched(true);
            if (!problem) {
              onSave({
                ...draft,
                name: draft.name.trim(),
                filterSets: draft.filterSets.map((set) => ({ ...set, label: set.label.trim() })),
              });
            }
          }}
        >
          {saving ? 'Saving…' : 'Save schedule'}
        </Button>
      </DialogActions>
    </>
  );
}

/**
 * Scheduled PDF emails for this Lens: the signed-in person's own schedules, a test send, and the
 * editor. svc-reporting makes the PDFs with the owner's access on the chosen day at 8am Sydney time.
 */
export default function LensSchedulesDialog({ open, onClose, lens, screenValues, filterOptions, userEmail }: Props) {
  const schedules = useLensSchedules(lens.id, open);
  const save = useSaveLensSchedule(lens.id);
  const remove = useDeleteLensSchedule(lens.id);
  const sendTest = useSendLensScheduleTest(lens.id);
  const [editing, setEditing] = useState<{ id?: string; draft: ScheduleDraft } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);

  const close = () => {
    setEditing(null);
    setConfirmingDelete(null);
    setMessage(null);
    save.reset();
    onClose();
  };
  const edit = (schedule?: LensSchedule) => {
    setMessage(null);
    save.reset();
    setEditing(
      schedule
        ? {
            id: schedule.id,
            draft: {
              name: schedule.name,
              dayOfMonth: schedule.dayOfMonth,
              period: schedule.period,
              filterSets: schedule.filterSets,
              recipients: schedule.recipients,
              enabled: schedule.enabled,
              includeInternal: schedule.includeInternal === true,
            },
          }
        : { draft: emptyDraft(lens, userEmail) },
    );
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={close}>
      <DialogTitle sx={{ pr: 6 }}>
        {editing ? (editing.id ? 'Edit scheduled email' : 'New scheduled email') : 'Scheduled emails'}
        <IconButton aria-label="Close" onClick={close} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {editing ? (
        <ScheduleEditor
          filterOptions={filterOptions}
          initial={editing.draft}
          lens={lens}
          saveError={save.error ? errorText(save.error) : null}
          saving={save.isPending}
          screenValues={screenValues}
          onCancel={() => setEditing(null)}
          onSave={async (draft) => {
            try {
              await save.mutateAsync({ id: editing.id, input: draft });
              setEditing(null);
              setMessage({
                severity: 'success',
                text: `Saved. It sends on the ${ordinal(draft.dayOfMonth)} of each month at 8am.`,
              });
            } catch {
              // Shown in the editor from save.error.
            }
          }}
        />
      ) : (
        <>
          <DialogContent dividers>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Email PDFs of this Lens on a day each month, one PDF per set of filters. They are made with your access,
              exactly as you would see the Lens, and go to Luxury Escapes addresses only.
            </Typography>
            {message && (
              <Alert severity={message.severity} sx={{ my: 1 }} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}
            {schedules.isLoading && <CircularProgress size={24} sx={{ my: 2 }} />}
            {schedules.isError && <Alert severity="error">{errorText(schedules.error)}</Alert>}
            {schedules.data?.length === 0 && (
              <Typography sx={{ my: 2 }} variant="body2">
                You have no scheduled emails on this Lens yet.
              </Typography>
            )}
            <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 1 }}>
              {(schedules.data ?? []).map((schedule) => (
                <Box key={schedule.id}>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    <Typography fontWeight={600} sx={{ flex: 1 }}>
                      {schedule.name}
                    </Typography>
                    {schedule.includeInternal && (
                      <Chip color="warning" label="Internal figures" size="small" variant="outlined" />
                    )}
                    <Chip
                      color={schedule.enabled ? 'success' : 'default'}
                      label={schedule.enabled ? 'On' : 'Paused'}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                  <Typography color="text.secondary" variant="body2">
                    {ordinal(schedule.dayOfMonth)} of each month, 8am · {schedule.filterSets.length} PDF
                    {schedule.filterSets.length === 1 ? '' : 's'} · to {schedule.recipients.join(', ')}
                  </Typography>
                  <Typography variant="body2">{runSummary(schedule.recentRuns[0])}</Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" onClick={() => edit(schedule)}>
                      Edit
                    </Button>
                    <Button
                      disabled={sendTest.isPending}
                      size="small"
                      onClick={async () => {
                        try {
                          await sendTest.mutateAsync(schedule.id);
                          setMessage({
                            severity: 'success',
                            text: `Test on its way to ${schedule.ownerEmail}. It usually arrives within a few minutes.`,
                          });
                        } catch (error) {
                          setMessage({ severity: 'error', text: errorText(error) });
                        }
                      }}
                    >
                      Send a test to me
                    </Button>
                    {confirmingDelete === schedule.id ? (
                      <>
                        <Button
                          color="error"
                          size="small"
                          variant="contained"
                          onClick={async () => {
                            try {
                              await remove.mutateAsync(schedule.id);
                              setMessage({ severity: 'success', text: `Deleted “${schedule.name}”.` });
                            } catch (error) {
                              setMessage({ severity: 'error', text: errorText(error) });
                            } finally {
                              setConfirmingDelete(null);
                            }
                          }}
                        >
                          Delete for good
                        </Button>
                        <Button size="small" onClick={() => setConfirmingDelete(null)}>
                          Keep
                        </Button>
                      </>
                    ) : (
                      <Button color="error" size="small" onClick={() => setConfirmingDelete(schedule.id)}>
                        Delete
                      </Button>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Close</Button>
            <Button variant="contained" onClick={() => edit()}>
              New scheduled email
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
